import type { LinksFunction, MetaFunction } from 'react-router';
import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration, useRouteError } from 'react-router';

import './tailwind.css';

export const links: LinksFunction = () => [
    { rel: 'icon', type: 'image/svg+xml', href: '/brand/atlanotes-icon-color.svg' },
    { rel: 'icon', type: 'image/x-icon', href: '/favicon.ico' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    {
        rel: 'preconnect',
        href: 'https://fonts.gstatic.com',
        crossOrigin: 'anonymous',
    },
    {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
    },
    {
        rel: 'stylesheet',
        href: 'https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@700&display=swap',
    },
];

export const meta: MetaFunction = () => [{ title: 'AtlaNotes — stories pinned to places' }];

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="en">
            <head>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <meta
                    name="description"
                    content="AtlaNotes is a shared map of memories. Pin notes, photos and videos to real places — past, present or future — and talk about them with your community."
                />
                <meta name="theme-color" content="#2e4259" />
                <Meta />
                <Links />
            </head>
            <body>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

export default function App() {
    return <Outlet />;
}

/** Shown while the app's JavaScript loads. */
export function HydrateFallback() {
    return (
        <div className="flex h-screen h-dvh flex-col items-center justify-center gap-4 bg-slate-50">
            <img
                src="/brand/atlanotes-icon-color.svg"
                alt=""
                aria-hidden="true"
                className="h-14 animate-pulse"
                draggable={false}
            />
            <p className="text-sm font-medium text-slate-500">Loading AtlaNotes…</p>
        </div>
    );
}

export function ErrorBoundary() {
    const error = useRouteError();
    const notFound = isRouteErrorResponse(error) && error.status === 404;

    return (
        <main className="flex min-h-screen flex-col items-center justify-center gap-4 bg-slate-50 p-6 text-center">
            <img src="/brand/atlanotes-icon-color.svg" alt="" aria-hidden="true" className="h-12" draggable={false} />
            <h1 className="text-2xl font-semibold text-slate-900">
                {notFound ? 'Page not found' : 'Something went wrong'}
            </h1>
            <p className="max-w-md text-slate-600">
                {notFound
                    ? "We couldn't find the page you were looking for."
                    : 'An unexpected error stopped this page from loading. Reloading usually fixes it.'}
            </p>
            {/* A full page load resets any broken client state. */}
            <a href="/" className="btn-primary">
                Back to the map
            </a>
        </main>
    );
}
